import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, get, set } from "@ember/object"
import { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import type {
  Card,
  CardStandards,
  CardTitleValueHtml,
  CardValueHtml,
  LegacyCardStackUsedAsTypes,
} from "@shared/models/card-stack"
import type { CommentRecipient } from "@shared/models/comment"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import { type ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import { cloneDeep, isNil } from "lodash-es"
import lessonCardCopyAction from "../../../../../lib/actions/lesson/LESSON_CARD_COPY"
import styles from "./actions.module.css"

interface ComponentState {
  newCommentText: null | string
  commentMenuIsOpen: boolean
  attachMenuIsOpen: boolean
  isPublicMenuIsOpen: boolean
  deleteMenuIsOpen: boolean
  isChangeColorMenuOpen: boolean
  menuIsOpen: boolean
}

@tagName("")
export default class Actions extends Component {
  readonly styles = styles

  modelsNeededForFirstEdit!: ModelsNeededForFirstEdit
  cardStackId!: string
  planbookId!: string
  cardStackUsedAs!: LegacyCardStackUsedAsTypes
  model!: CardTitleValueHtml | CardValueHtml | CardStandards

  @service("ember-store") declare store: EmberStore

  @service declare dispatcher: Dispatcher
  @service session
  @service dialog
  @service notifier
  @service lessonEditor!: LessonEditor

  state: ComponentState = {
    newCommentText: null,
    commentMenuIsOpen: false,
    attachMenuIsOpen: false,
    isPublicMenuIsOpen: false,
    deleteMenuIsOpen: false,
    isChangeColorMenuOpen: false,
    menuIsOpen: false,
  }

  menuConstraints = [{ to: "window", pin: ["left", "bottom"] }]

  @action
  copySection(section) {
    if (get(get(this, "session"), "isAuthenticated") !== true) {
      get(this, "session").mustSignInToProceed("Copy Lesson Card")
      return
    }
    this.dispatcher.dispatchPreparedAction(
      lessonCardCopyAction({
        currentUserId: get(get(this, "session"), "userId"),
        cardStackId: get(this, "cardStackId"),
        section: section,
      })
    )
    this.notifier.success("", {
      title: `Card Copied!`,
      message: "Click a card's paste icon to paste.",
      duration: 2000,
    })
  }

  @action
  pasteSection(card) {
    if (!isNil(this.session.userId)) {
      let currentUser = this.store.find("user", this.session.userId).then((user) => {
        if (isNil(user?.attributes?.clipboard?.card)) return

        let sectionToPaste = cloneDeep(user.attributes.clipboard.card)
        this.get("lessonEditor").pasteCard(
          this.get("modelsNeededForFirstEdit"),
          get(this, "cardStackId"),
          card.id,
          card,
          sectionToPaste
        )
      })
    }
  }

  @action
  openCommentMenu() {
    set(get(this, "state"), "menuIsOpen", true)
    set(get(this, "state"), "commentMenuIsOpen", true)
  }

  @action
  closeCommentMenu() {
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "commentMenuIsOpen", false)
  }

  @action
  addComment(commentText) {
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "commentMenuIsOpen", false)
    let section = get(this, "model")

    // We do this primarily because this click event is bizarrely triggered
    // twice in the test. I have no idea why that is.
    if (isNone(commentText)) return

    if (!isNil(this.session.userId)) {
      let currentUser = this.store.find("user", this.session.userId).then((user) => {
        const recipients: CommentRecipient[] = [
          {
            type: "planbook",
            id: this.get("planbookId"),
          },
        ]
        this.get("lessonEditor").addComment(
          this.get("modelsNeededForFirstEdit"),
          this.get("planbookId"),
          this.get("cardStackId"),
          section,
          commentText,
          user,
          recipients,
          []
        )
        set(get(this, "state"), "newCommentText", "")
      })
    }
  }

  @action
  addAttachments(section: Card, source: string, files: []) {
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "attachMenuIsOpen", false)
    this.get("lessonEditor").addAttachments(
      this.get("modelsNeededForFirstEdit"),
      files,
      get(this, "cardStackId"),
      section.id,
      get(get(get(this, "model"), "attributes"), "attachments"),
      source
    )
  }

  @action
  addAttachmentAsLink(section: Card) {
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "attachMenuIsOpen", false)
    this.dialog
      .blank("dialogs/link-as-attachment", {
        className: "dialog--create-annotation",
        title: null,
        url: null,
      })
      .then((response) => {
        let files = [{ url: response.url, title: response.title, type: "link" }]
        this.get("lessonEditor").addAttachments(
          this.get("modelsNeededForFirstEdit"),
          files,
          get(this, "cardStackId"),
          section.id,
          get(get(get(this, "model"), "attributes"), "attachments"),
          "link"
        )
      })
  }

  @action
  changeCardColor(color) {
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "isChangeColorMenuOpen", false)
    this.get("lessonEditor").changeCardColor(
      this.get("modelsNeededForFirstEdit"),
      color,
      get(this, "cardStackId"),
      get(get(this, "model"), "id"),
      get(get(get(this, "model"), "attributes"), "color")
    )
  }

  @action
  openAttachMenu() {
    if (this.isDestroyed || this.isDestroying) return
    set(get(this, "state"), "menuIsOpen", true)
    set(get(this, "state"), "attachMenuIsOpen", true)
  }

  @action
  closeAttachMenu() {
    if (this.isDestroyed || this.isDestroying) return
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "attachMenuIsOpen", false)
  }

  @action
  openedAttachDialog() {
    if (this.isDestroyed || this.isDestroying) return
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "attachMenuIsOpen", false)
  }

  @action
  toggleChangeColorMenu() {
    set(get(this, "state"), "menuIsOpen", !get(get(this, "state"), "isChangeColorMenuOpen"))
    set(
      get(this, "state"),
      "isChangeColorMenuOpen",
      !get(get(this, "state"), "isChangeColorMenuOpen")
    )
  }

  @action
  openIsPublicMenu() {
    set(get(this, "state"), "menuIsOpen", true)
    set(get(this, "state"), "isPublicMenuIsOpen", true)
  }

  @action
  closeIsPublicMenu() {
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "isPublicMenuIsOpen", false)
  }

  @action
  showOnClassWebsite(section) {
    set(get(this, "state"), "isPublicMenuIsOpen", false)
    set(get(this, "state"), "menuIsOpen", false)

    this.get("lessonEditor").toggleClassWebsite(
      this.get("modelsNeededForFirstEdit"),
      true,
      get(this, "cardStackId"),
      section.id
    )
  }

  @action
  hideOnClassWebsite(section) {
    set(get(this, "state"), "isPublicMenuIsOpen", false)
    set(get(this, "state"), "menuIsOpen", false)

    this.get("lessonEditor").toggleClassWebsite(
      this.get("modelsNeededForFirstEdit"),
      false,
      get(this, "cardStackId"),
      section.id
    )
  }

  @action
  openDeleteMenu() {
    set(get(this, "state"), "menuIsOpen", true)
    set(get(this, "state"), "deleteMenuIsOpen", true)
  }

  @action
  closeDeleteMenu() {
    set(get(this, "state"), "menuIsOpen", false)
    set(get(this, "state"), "deleteMenuIsOpen", false)
  }

  @action
  removeSection(section) {
    // closeDelete needs to come first to avoid a race condition. If the lesson is a template/unedited
    // and the user deletes the bottom card, there is a timing conflict with the template being replaced
    // with an actual lesson and the card being deleted
    this.closeDeleteMenu()
    this.get("lessonEditor").removeCard(
      this.get("modelsNeededForFirstEdit"),
      get(this, "cardStackId"),
      section
    )
  }
}
